import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import classNames from 'classnames';
import { faInfo, faPlusCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Formik, FormikHelpers } from 'formik';
import { Button, Col, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { fetchEducationIndustries, fetchEducationOthers } from '../../store/createApplicationSlice';
import { RootState } from '../../store/rootReducer';
import { MyApplicationDetail } from '../../api/openapi';
import { AppDispatch } from '../../store/store';
import { restValidationToForm, setYupLocale } from '../../utils/formUtils';
import ApplicationCourseCreate from './ApplicationCourseCreate';
import { applicationApi } from '../../api';

interface Props {
  item: MyApplicationDetail;
  onSaveSuccess: () => void;
}

interface Values {
  educationIndustry: {
    label: string;
    value: string;
  }[];
  educationOther: {
    label: string;
    value: string;
  }[];
  course: {
    name: string;
  }[];
  isMatriculationFinished: boolean | undefined;
  isJourneymanFinished: boolean | undefined;
  isContractFinished: boolean | undefined;
  isEducationAbroadFinished: boolean | undefined;
  hasOngoingContract: boolean | undefined;
  otherEducationInformation: string;
}

const ApplicationEducationEdit: React.FC<Props> = ({ item, onSaveSuccess }: Props) => {
  const { t, i18n } = useTranslation(['viewApplication', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const [validationSchema, setValidationSchema] = useState<Yup.AnySchema | null>(null);
  const { educationIndustries, educationIndustriesLoading, educationIndustriesError } = useSelector((state: RootState) => state.createApplication);
  const { educationOthers, educationOthersLoading, educationOthersError } = useSelector((state: RootState) => state.createApplication);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchEducationIndustries());
    dispatch(fetchEducationOthers());
  }, [dispatch]);

  useEffect(() => {
    setYupLocale(Yup, i18n.language);
    setValidationSchema(
      Yup.object().shape({
        educationIndustry: Yup.array()
          .of(
            Yup.object().shape({
              label: Yup.string(),
              value: Yup.string(),
            })
          )
          .label(t('educationIndustry'))
          .required()
          .when('isJourneymanFinished', (isJourneymanFinished: boolean, schema: Yup.ArraySchema<Yup.AnySchema>) => {
            return isJourneymanFinished ? schema.min(1, t('educationIndustryIsRequired')) : schema;
          }),

        isMatriculationFinished: Yup.boolean().label(t('isMatriculationFinished')).nullable(),
        isEducationAbroadFinished: Yup.boolean().label(t('isEducationAbroadFinished')).nullable(),
        isJourneymanFinished: Yup.boolean().label(t('isJourneymanFinished')).nullable(),
        isContractFinished: Yup.boolean().label(t('isContractFinished')).nullable(),
        hasOngoingContract: Yup.boolean().label(t('hasOngoingContract')).nullable(),
      })
    );
  }, [t, i18n.language]);

  return (
    <>
      <Formik<Values>
        initialValues={{
          otherEducationInformation: item.otherEducationInformation || '',
          isMatriculationFinished: item.isMatriculationFinished != null ? item.isMatriculationFinished : undefined,
          isJourneymanFinished: item.isJourneymanFinished != null ? item.isJourneymanFinished : undefined,
          isContractFinished: item.isContractFinished != null ? item.isContractFinished : undefined,
          hasOngoingContract: item.hasOngoingContract != null ? item.hasOngoingContract : undefined,
          isEducationAbroadFinished: item.isEducationAbroadFinished != null ? item.isEducationAbroadFinished : undefined,
          educationIndustry: item.education
            .filter((e) => e.educationIndustry != null)
            .map((e) => ({
              value: e.educationIndustry?.id || '',
              label: e.educationIndustry?.name || '',
            })),
          educationOther:
            item.educationOther
              ?.filter((e) => e.educationOther != null)
              .map((e) => ({
                value: e.educationOther?.id || '',
                label: e.educationOther?.name || '',
              })) || [],
          course: item.course.map((c) => ({
            name: c.name,
          })),
        }}
        validateOnBlur={false}
        // validateOnChange={false}
        validationSchema={validationSchema}
        // enableReinitialize
        onSubmit={async (values: Values, { setSubmitting, setFieldError }: FormikHelpers<Values>) => {
          try {
            setIsLoading(true);
            await applicationApi.myApplicationPartialUpdate({
              uuid: item.id,
              patchedMyApplicationDetailRequest: {
                education: values.educationIndustry.map((e) => ({
                  educationIndustry: {
                    id: e.value,
                  },
                })),

                educationOther: values.educationOther.map((e) => ({
                  educationOther: {
                    id: e.value,
                  },
                })),
                course: values.course.map((c) => ({
                  name: c.name,
                })),
                isMatriculationFinished: values.isMatriculationFinished != null ? values.isMatriculationFinished : null,
                isContractFinished: values.isContractFinished != null ? values.isContractFinished : null,
                isJourneymanFinished: values.isJourneymanFinished != null ? values.isJourneymanFinished : null,
                hasOngoingContract: values.hasOngoingContract != null ? values.hasOngoingContract : null,
                isEducationAbroadFinished: values.isEducationAbroadFinished != null ? values.isEducationAbroadFinished : null,
                otherEducationInformation: values.otherEducationInformation,
              },
            });

            toast.success(t('applicationUpdate'));

            onSaveSuccess();
          } catch (e) {
            restValidationToForm({
              t,
              errorResponse: e.response,
              setFieldError,
              errorFieldMap: {},
            });
          } finally {
            setIsLoading(false);
            setSubmitting(false);
          }
        }}
      >
        {({ values, touched, errors, getFieldProps, setFieldValue, setStatus, handleReset, handleSubmit }): React.ReactElement => (
          <Form
            noValidate
            onReset={handleReset}
            onSubmit={(e: React.FormEvent<HTMLFormElement>): void => {
              setStatus('submitted');
              handleSubmit(e);
            }}
          >
            <Form.Row>
              <Col md="4">
                <p className="small text-muted">{t('educationHelp')}</p>
              </Col>
              <Col md="3">
                <Form.Label className="textBold">{t('isMatriculationFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isMatriculationFinished-yes"
                  name="isMatriculationFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isMatriculationFinished === true}
                  onChange={(e) => setFieldValue('isMatriculationFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isMatriculationFinished-no"
                  name="isMatriculationFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isMatriculationFinished === false}
                  onChange={(e) => setFieldValue('isMatriculationFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isMatriculationFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
              <Col md="3">
                <Form.Label className="textBold">{t('isContractFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isContractFinished-yes"
                  name="isContractFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isContractFinished === true}
                  onChange={(e) => setFieldValue('isContractFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isContractFinished-no"
                  name="isContractFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isContractFinished === false}
                  onChange={(e) => setFieldValue('isContractFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isContractFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
              <Col md="2">
                <Form.Label className="textBold">{t('hasOngoingContract')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="hasOngoingContract-yes"
                  name="hasOngoingContract"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.hasOngoingContract === true}
                  onChange={(e) => setFieldValue('hasOngoingContract', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="hasOngoingContract-no"
                  name="hasOngoingContract"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.hasOngoingContract === false}
                  onChange={(e) => setFieldValue('hasOngoingContract', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="hasOngoingContract" component="div" className="invalid-feedback d-block text-left" />
              </Col>
            </Form.Row>
            <br />
            <Form.Row>
              <Col md="4" />
              <Col md="3">
                <Form.Label className="textBold">{t('isJourneymanFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isJourneymanFinished-yes"
                  name="isJourneymanFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isJourneymanFinished === true}
                  onChange={(e) => setFieldValue('isJourneymanFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isJourneymanFinished-no"
                  name="isJourneymanFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isJourneymanFinished === false}
                  onChange={(e) => setFieldValue('isJourneymanFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isJourneymanFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
              <Col md="3">
                <Form.Label className="textBold">{t('isEducationAbroadFinished')}*</Form.Label>
                <br />
                <Form.Check
                  custom
                  id="isEducationAbroadFinished-yes"
                  name="isEducationAbroadFinished"
                  inline
                  label={t('common:yes')}
                  type="checkbox"
                  checked={values.isEducationAbroadFinished === true}
                  onChange={(e) => setFieldValue('isEducationAbroadFinished', e.target.checked ? true : undefined)}
                />
                <Form.Check
                  custom
                  id="isEducationAbroadFinished-no"
                  name="isEducationAbroadFinished"
                  inline
                  label={t('common:no')}
                  type="checkbox"
                  checked={values.isEducationAbroadFinished === false}
                  onChange={(e) => setFieldValue('isEducationAbroadFinished', e.target.checked ? false : undefined)}
                />
                <ErrorMessage name="isEducationAbroadFinished" component="div" className="invalid-feedback d-block text-left" />
              </Col>
            </Form.Row>
            <br />
            <Form.Row>
              <Col md="4" />
              <Col>
                {values.isJourneymanFinished && (
                  <Form.Group controlId="educationIndustry">
                    <Form.Label className="textBold">{t('educationIndustry')}*:</Form.Label>
                    <Select
                      options={
                        educationIndustries
                          ? educationIndustries.map((e) => ({
                              value: e.id,
                              label: e.name,
                            }))
                          : []
                      }
                      isMulti
                      value={values.educationIndustry}
                      onChange={(v) => setFieldValue('educationIndustry', v)}
                      placeholder={t('chooseEducationIndustry')}
                      className={classNames({
                        'is-invalid': touched.educationIndustry && errors.educationIndustry,
                      })}
                    />
                    {educationIndustriesError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
                    {educationIndustriesLoading === 'idle' && educationIndustries?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noSchools')}</div>}
                    <Form.Text className="text-muted">{educationIndustriesError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
                    <ErrorMessage name="educationIndustry" component="div" className="invalid-feedback d-block text-left" />
                  </Form.Group>
                )}
                <Form.Group controlId="educationOther">
                  <Form.Label className="textBold">{t('educationOther')}:</Form.Label>
                  <Select
                    options={
                      educationOthers
                        ? educationOthers.map((e) => ({
                            value: e.id,
                            label: e.name,
                          }))
                        : []
                    }
                    isMulti
                    value={values.educationOther}
                    onChange={(v) => setFieldValue('educationOther', v)}
                    placeholder={t('chooseEducationOther')}
                  />
                  {educationOthersError && <div className="invalid-feedback d-block text-left">{t('common:kerfisvilla')}</div>}
                  {educationOthersLoading === 'idle' && educationOthers?.length === 0 && <div className="invalid-feedback d-block text-left">{t('noEducation')}</div>}
                  <Form.Text className="text-muted">{educationOthersError === 'pending' && <FontAwesomeIcon spin icon={faSpinner} />}</Form.Text>
                  <ErrorMessage name="educationOther" component="div" className="invalid-feedback d-block text-left" />
                </Form.Group>
                <Form.Group>
                  <Form.Label>
                    <OverlayTrigger placement="right-end" overlay={<Tooltip id="tooltip-courses">{t('EducationCoursesInfo')}</Tooltip>}>
                      <div className="textBold">
                        {t('educationCourses')}: <FontAwesomeIcon icon={faInfo} />
                      </div>
                    </OverlayTrigger>
                  </Form.Label>
                  {values.course.map((_, i) => (
                    <ApplicationCourseCreate key={`course-${i}`} index={i} />
                  ))}

                  <Button
                    type="button"
                    variant="secondary"
                    className="float-right"
                    onClick={(e) =>
                      setFieldValue('course', [
                        ...values.course,
                        {
                          name: '',
                        },
                      ])
                    }
                  >
                    <FontAwesomeIcon icon={faPlusCircle} /> {t('addCourse')}
                  </Button>
                </Form.Group>
                <Form.Group controlId="otherEducationInformation">
                  <Form.Label>
                    <OverlayTrigger placement="right-end" overlay={<Tooltip id="tooltip-other">{t('EducationOtherInfo')}</Tooltip>}>
                      <div className="textBold">
                        {t('otherEducationInformation')}: <FontAwesomeIcon icon={faInfo} />
                      </div>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    type="text"
                    className={classNames({
                      'is-invalid': touched.otherEducationInformation && errors.otherEducationInformation,
                    })}
                    {...getFieldProps('otherEducationInformation')}
                  />
                  <ErrorMessage name="otherEducationInformation" component="div" className="invalid-feedback d-block text-left" />
                </Form.Group>
                <hr />
                <Button
                  disabled={isLoading}
                  variant="primary"
                  type="button"
                  onClick={() => {
                    setStatus('submitted');
                    handleSubmit();
                  }}
                >
                  {t('common:save')}
                </Button>
              </Col>
            </Form.Row>
          </Form>
        )}
      </Formik>
    </>
  );
};
export default ApplicationEducationEdit;
